import { VALUES_TO_REDACT_REGEX } from './keys';

/**
 * Strips query-param keys that match VALUES_TO_REDACT_REGEX from a URL string, which may be
 * absolute, root-relative, non-rooted-relative, or protocol-relative (`//host/path`). Used by
 * `redactForLogging` when the object walk hits a string leaf under a URL-shaped key (see
 * `URL_KEY_REGEX`), since a URL is a plain string and therefore invisible to the object-key
 * traversal on its own.
 *
 * Operates on the raw string rather than parsing it with `new URL(...)`: reconstructing the URL
 * from a parsed object's parts (origin/pathname/search/hash) can't tell a root-relative reference
 * apart from a non-rooted one (both parse to the same pathname against a placeholder base) and
 * loses a protocol-relative authority entirely, so this instead splits on the first `?`/`#` and
 * rewrites only the query portion, leaving everything else byte-for-byte as given.
 */
export function redactUrlParams(url: string): string {
	const hashIndex = url.indexOf('#');
	const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
	const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

	const queryIndex = beforeHash.indexOf('?');
	if (queryIndex === -1) return url;
	const base = beforeHash.slice(0, queryIndex);
	const params = new URLSearchParams(beforeHash.slice(queryIndex + 1));

	let changed = false;
	for (const key of [...params.keys()]) {
		if (VALUES_TO_REDACT_REGEX.test(key)) {
			params.set(key, '[Redacted]');
			changed = true;
		}
	}
	if (!changed) return url;
	return `${base}?${params.toString()}${hash}`;
}
